import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Subject as SubjectIcon,
  Group as GroupIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../../utils/fetchJson';
import AppLoader from '../../../components/AppLoader';
import { usePhase } from '../../../contexts/PhaseContext';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';

const SubjectAssignmentsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const TAB_DEPARTMENTS = 'departments';
  const TAB_TEACHER_SUBJECTS = 'teacherSubjects';
  const DEPT_TAB_DEPARTMENTS = 'departments_list';
  const DEPT_TAB_DEPARTMENT_SUBJECTS = 'department_subjects';
  const DEPT_TAB_DEPARTMENT_HEADS = 'department_heads';
  const DEPT_TAB_DEPARTMENT_REPORTS = 'department_reports';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const phaseCode = (localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toString().trim().toLowerCase();
      return phaseCode === 'sec' ? TAB_DEPARTMENTS : TAB_TEACHER_SUBJECTS;
    } catch (_) {
      return TAB_TEACHER_SUBJECTS;
    }
  });
  const [subjects, setSubjects] = useState([]);
  const [error, setError] = useState(null);
  const { activePhase, activePhaseId } = usePhase();
  const reqSeq = useRef(0);
  const [teachers, setTeachers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subjectGroups, setSubjectGroups] = useState([]);
  const [departmentReports, setDepartmentReports] = useState([]);
  const [departmentsTab, setDepartmentsTab] = useState(DEPT_TAB_DEPARTMENTS);
  const resolvedPhaseCode = useMemo(() => {
    const fromCtx = activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase');
    return fromCtx ? String(fromCtx).toLowerCase() : '';
  }, [activePhase]);

  const isSecPhase = resolvedPhaseCode === 'sec';

  // Nursery/Primary: Subject Assignments page is not needed (class teachers inherit subjects from class assignments).
  useEffect(() => {
    if (resolvedPhaseCode && !isSecPhase) {
      navigate('/subject-management', { replace: true });
    }
  }, [resolvedPhaseCode, isSecPhase, navigate]);

  const resolvedPhaseId = useMemo(() => {
    const fromCtx = activePhaseId || localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId');
    const parsed = parseInt(fromCtx, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [activePhaseId]);

  // Ensure non-sec phases never land on Departments tab
  useEffect(() => {
    if (!isSecPhase && activeTab === TAB_DEPARTMENTS) setActiveTab(TAB_TEACHER_SUBJECTS);
  }, [isSecPhase, activeTab]);

  const filterByPhase = useCallback((records) => {
    if (!Array.isArray(records) || (!resolvedPhaseCode && !resolvedPhaseId)) return Array.isArray(records) ? records : [];
    return records.filter((record) => {
      if (!record) return false;
      const recPhaseId = Number(record.phaseId ?? record.phaseID ?? record.phase_id ?? record.PhaseId);
      if (resolvedPhaseId && recPhaseId) return recPhaseId === resolvedPhaseId;
      const recPhaseCode = (record.phaseCode || record.phase || record.cycleType || record.level || '').toString().toLowerCase();
      if (resolvedPhaseCode && recPhaseCode) return recPhaseCode === resolvedPhaseCode;
      return true;
    });
  }, [resolvedPhaseCode, resolvedPhaseId]);
  
  // Departments
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [departmentFormData, setDepartmentFormData] = useState({ name: '' });

  const [deptSubjectsDialogOpen, setDeptSubjectsDialogOpen] = useState(false);
  const [deptSubjectsDepartmentId, setDeptSubjectsDepartmentId] = useState('');
  const [deptSubjectsSelectedGroupIds, setDeptSubjectsSelectedGroupIds] = useState([]);

  const [deptHeadDialogOpen, setDeptHeadDialogOpen] = useState(false);
  const [deptHeadDepartment, setDeptHeadDepartment] = useState(null);
  const [deptHeadFormData, setDeptHeadFormData] = useState({ subjectHeadId: '' });
  // Teacher-Subject Assignment
  const [teacherSubjectDialogOpen, setTeacherSubjectDialogOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [teacherSubjectAssignments, setTeacherSubjectAssignments] = useState({});
  // Local dialog-only subjects selection so Cancel won't persist changes
  const [dialogTeacherSubjects, setDialogTeacherSubjects] = useState([]);

  // UI helpers for avatars and names
  const stringToColor = useCallback((str) => {
    if (!str) return '#90caf9';
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 60%)`;
  }, []);
  const getDisplayName = useCallback((u) => {
  if (!u) return t('subjectAssignments.labels.notAssigned');

  const clean = (v) => {
    const s = (v ?? '').toString().trim();
    if (!s) return '';
    const low = s.toLowerCase();
    if (low === 'undefined' || low === 'null' || low === 'nan') return '';
    return s;
  };

  // Preferred explicit fields
  const displayName = clean(u.displayName || u.preferredName);
  if (displayName) return displayName;

  // Full name variants
  const fullName = clean(u.fullName || u.full_name);
  if (fullName) return fullName;

  const name = clean(u.name);
  if (name) return name;

  // First + last variants
  const first = clean(u.firstName || u.first_name);
  const last  = clean(u.lastName || u.last_name);
  const combined = [first, last].filter(Boolean).join(' ').trim();
  if (combined) return combined;

  // Username then email
  const username = clean(u.username);
  if (username) return username;
  const email = clean(u.email);
  if (email) return email;

  // Stable fallback
  if (u.id) return t('subjectAssignments.labels.userWithId', { id: u.id });
  return t('subjectAssignments.labels.notAssigned');
}, [t]);
  const getInitials = useCallback((name) => {
    const n = (name || 'T').trim();
    if (!n) return 'T';
    const parts = n.split(/\s+/).filter(Boolean);
    const first = parts[0] ? parts[0][0] : '';
    const second = parts[1] ? parts[1][0] : '';
    return `${first}${second}`.toUpperCase() || 'T';
  }, []);
  
  const getTeacherName = useCallback((teacherId) => {
    if (!teacherId) return t('subjectAssignments.labels.notAssigned');
    const tid = String(teacherId);
    const poolA = Array.isArray(teachers) ? teachers : [];
    const poolB = Array.isArray(allUsers) ? allUsers : [];
    const teacher = poolA.find(x => String(x.id) === tid) || poolB.find(x => String(x.id) === tid);
    if (!teacher) return t('subjectAssignments.labels.userWithId', { id: tid });
    const name = getDisplayName(teacher);
    return name || t('subjectAssignments.labels.userWithId', { id: tid });
  }, [allUsers, getDisplayName, t, teachers]);

  const dedupedSubjectGroups = useMemo(() => {
    const seen = new Set();
    return (Array.isArray(subjectGroups) ? subjectGroups : []).filter((group) => {
      const key = group?.id != null ? `id:${group.id}` : `name:${String(group?.name || '').trim().toLowerCase()}|dept:${group?.departmentId || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [subjectGroups]);

  const getSubjectGroupLabel = useCallback((group) => {
    if (!group) return t('common.unnamedSubject');
    return group.name || group.code || `Group-${group.id}`;
  }, [t]);

  const formatDepartmentReportMonth = useCallback((month, year) => {
    const numericMonth = Number(month);
    const numericYear = Number(year);
    if (Number.isFinite(numericMonth) && numericMonth >= 1 && numericMonth <= 12) {
      try {
        return new Date(Number.isFinite(numericYear) ? numericYear : 2000, numericMonth - 1, 1).toLocaleString(undefined, {
          month: 'long',
          year: Number.isFinite(numericYear) ? 'numeric' : undefined,
        });
      } catch (_) {}
    }
    return [month, year].filter(Boolean).join(' / ') || '—';
  }, []);

  const formatDepartmentReportDate = useCallback((value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString();
    } catch (_) {
      return String(value);
    }
  }, []);

  const resolveBackendBaseUrl = useCallback(() => {
    const configuredBaseRaw = (
      process.env.REACT_APP_API_BASE_URL ||
      process.env.VITE_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      ''
    ).trim();
    if (configuredBaseRaw) return configuredBaseRaw.replace(/\/$/, '');
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return String(window.location.origin).replace(/\/$/, '');
    }
    return '';
  }, []);

  const resolveReportFileUrl = useCallback((fileUrl) => {
    if (!fileUrl) return '';
    const raw = String(fileUrl).trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    let base = resolveBackendBaseUrl();
    if (!base) return raw;

    // Uploaded files are served at /uploads (NOT /api/uploads). Many deployments set
    // REACT_APP_API_BASE_URL to something ending with /api, so we must strip it here.
    if (/^\/?uploads(\b|\/)/i.test(raw) || raw.startsWith('/uploads/')) {
      base = base.replace(/\/api$/i, '');
    }
    const pathPart = raw.startsWith('/') ? raw : `/${raw}`;
    return `${base}${pathPart}`;
  }, [resolveBackendBaseUrl]);

  // Removed Class-Subject state and helpers

  // Timeout wrapper to prevent hanging requests
  const withTimeout = useCallback((promise, ms = 8000) => {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Request timeout')), ms);
      promise
        .then((v) => { clearTimeout(timer); resolve(v); })
        .catch((e) => { clearTimeout(timer); reject(e); });
    });
  }, []);
  const fetchData = useCallback(async () => {
    const mySeq = ++reqSeq.current;
    let maxGuard;
    try {
      setError(null);
      setLoading(true);
      maxGuard = setTimeout(() => { if (reqSeq.current === mySeq) setLoading(false); }, 2000);
      
      // Build phase-scoped cache keys
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseId = resolvedPhaseId ? String(resolvedPhaseId) : (localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '');
      const phaseCode = resolvedPhaseCode;
      const ctx = `${schoolId}|${phaseId || phaseCode || '-'}`;
      const kSubjects = `subjectAssignments:subjects:${ctx}`;
      const kTeachers = `subjectAssignments:teachers:${ctx}`;
      const kTsMap    = `subjectAssignments:teacherSubjects:${ctx}`;

      // Prefill from cache to avoid flash
      let prefilled = false;
      try { const raw = localStorage.getItem(kSubjects); if (raw) { const v = JSON.parse(raw); if (Array.isArray(v)) { setSubjects(v); if (v.length) prefilled = true; } } } catch(_) {}
      try { const raw = localStorage.getItem(kTeachers); if (raw) { const v = JSON.parse(raw); if (Array.isArray(v)) { setTeachers(v); if (v.length) prefilled = true; } } } catch(_) {}
      try { const raw = localStorage.getItem(kTsMap);   if (raw) { const v = JSON.parse(raw); if (v && typeof v==='object') { setTeacherSubjectAssignments(v); prefilled = true; } } } catch(_) {}

      if (prefilled && reqSeq.current === mySeq) setLoading(false);

      // Fetch in parallel using verified endpoints (allocations + users)

      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
      const baseHeaders = schoolId
        ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId || undefined }
        : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId || undefined };
      const headers = {
        ...baseHeaders,
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'Accept': 'application/json'
      };
      const qsTail = [
        phaseCode ? `phase=${encodeURIComponent(phaseCode)}` : '',
        schoolId ? `schoolId=${encodeURIComponent(schoolId)}` : ''
      ].filter(Boolean).join('&');
      const settled = await Promise.allSettled([
        withTimeout(fetchJson('/api/allocations/subjects', { headers })),
        withTimeout(fetchJson(`/api/users${qsTail ? `?${qsTail}` : ''}`, { headers })),
        withTimeout(fetchJson('/api/teacher-subjects', { headers })),
        isSecPhase ? withTimeout(fetchJson('/api/departments', { headers })) : Promise.resolve([]),
        isSecPhase ? withTimeout(fetchJson('/api/subject-groups?includeVariants=0', { headers })) : Promise.resolve([]),
        isSecPhase ? withTimeout(fetchJson('/api/departments/reports', { headers })) : Promise.resolve([])
      ]);

      let subjectsData      = settled[0].status === 'fulfilled' ? settled[0].value : [];
      const usersResp         = settled[1].status === 'fulfilled' ? settled[1].value : [];
      const teacherSubjectsData = settled[2].status === 'fulfilled' ? settled[2].value : [];
      const departmentsData = settled[3].status === 'fulfilled' ? settled[3].value : [];
      const subjectGroupsData = settled[4].status === 'fulfilled' ? settled[4].value : [];
      const departmentReportsData = settled[5].status === 'fulfilled' ? settled[5].value : [];

      let subjectsArray = Array.isArray(subjectsData) ? subjectsData : (subjectsData?.rows || subjectsData?.subjects || subjectsData?.data || []);
      // Fallbacks if allocations return empty
      if (!Array.isArray(subjectsArray) || subjectsArray.length === 0) {
        try {
          const scopedFallback = await withTimeout(fetchJson(`/api/subjects?include=category${qsTail ? `&${qsTail}` : ''}`, { headers }), 12000);
          subjectsArray = Array.isArray(scopedFallback) ? scopedFallback : (scopedFallback?.subjects || scopedFallback?.rows || scopedFallback?.data || []);
        } catch (scopedErr) {
          console.warn('[SubjectAssignmentsPage] Scoped subjects fallback failed', scopedErr);
        }
      }
      const usersArray = Array.isArray(usersResp?.rows) ? usersResp.rows : (Array.isArray(usersResp) ? usersResp : (usersResp?.users || usersResp?.data || []));
      let teachersArray = usersArray.filter(u => {
        // Normalize possible role shapes
        const roleName = (u.role?.name || u.role || u.roleName || '').toString().toLowerCase();
        const roleId = Number(u.roleId || u.RoleId || u.role_id || 0);
        const rolesArr = Array.isArray(u.roles) ? u.roles : [];
        const rolesHasTeacher = rolesArr.some(r => ((r?.name || r)?.toString().toLowerCase() === 'teacher'));
        return roleName === 'teacher' || roleId === 3 || rolesHasTeacher;
      });
      let teacherSubjectsArray = Array.isArray(teacherSubjectsData) ? teacherSubjectsData : (teacherSubjectsData?.teacherSubjects || teacherSubjectsData?.data || []);

      // Organize maps
      const teacherAssignments = {};
      teacherSubjectsArray = filterByPhase(teacherSubjectsArray);
      const filteredSubjects = filterByPhase(subjectsArray);
      const filteredTeachers = filterByPhase(teachersArray);

      teacherSubjectsArray.forEach(ts => {
        const tId = Number(ts.teacherId);
        const sId = Number(ts.subjectId);
        if (!tId || !sId) return;
        if (!teacherAssignments[tId]) teacherAssignments[tId] = [];
        if (!teacherAssignments[tId].includes(sId)) teacherAssignments[tId].push(sId);
      });
      // Removed class assignments composition

      if (reqSeq.current === mySeq) {
        setSubjects(filteredSubjects);
        setTeachers(filteredTeachers);
        setAllUsers(filterByPhase(usersArray));
        setTeacherSubjectAssignments(teacherAssignments);
        if (isSecPhase) {
          setDepartments(Array.isArray(departmentsData) ? departmentsData : (departmentsData?.rows || departmentsData?.data || []));
          setSubjectGroups(Array.isArray(subjectGroupsData) ? subjectGroupsData : (subjectGroupsData?.rows || subjectGroupsData?.data || []));
          setDepartmentReports(Array.isArray(departmentReportsData) ? departmentReportsData : (departmentReportsData?.rows || departmentReportsData?.data || []));
        } else {
          setDepartments([]);
          setSubjectGroups([]);
          setDepartmentReports([]);
        }
        // Update cache
        try { localStorage.setItem(kSubjects, JSON.stringify(subjectsArray)); } catch(_) {}
        try { localStorage.setItem(kTeachers, JSON.stringify(teachersArray)); } catch(_) {}
        try { localStorage.setItem(kTsMap,    JSON.stringify(teacherAssignments)); } catch(_) {}
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setError(t('subjectAssignments.messages.fetchDataFailed', { message: error?.message || t('subjectAssignments.labels.unknown') }));
    } finally {
      if (reqSeq.current === mySeq) setLoading(false);
      if (maxGuard) {
        clearTimeout(maxGuard);
      }
    }
  }, [resolvedPhaseCode, resolvedPhaseId, filterByPhase, withTimeout]);

  // Trigger initial and phase-change fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useScopeAutoRefresh([fetchData]);
  
  // Prefetch any missing user records referenced by departments (department heads)
  useEffect(() => {
    const doPrefetch = async () => {
      try {
        if (!isSecPhase) return;
        const ids = Array.from(new Set((departments || []).map(d => d && d.subjectHeadType === 'user' ? d.subjectHeadId : null).filter(Boolean))).map(String);
        if (!ids.length) return;
        const have = new Set([
          ...((Array.isArray(teachers) ? teachers : []).map(u => String(u.id))),
          ...((Array.isArray(allUsers) ? allUsers : []).map(u => String(u.id)))
        ]);
        const missing = ids.filter(id => !have.has(id));
        if (!missing.length) return;

        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
        const headers = {
          ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
          ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
          ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {}),
          ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}),
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          'Accept': 'application/json'
        };

        const fetched = [];
        for (const id of missing) {
          try { const u = await fetchJson(`/api/users/${id}`, { headers }); if (u) fetched.push(u); } catch (_) {}
        }
        if (fetched.length) {
          setAllUsers(prev => {
            const byId = new Map([...(prev || []).map(x => [String(x.id), x])]);
            for (const u of fetched) byId.set(String(u.id), u);
            return Array.from(byId.values());
          });
        }
      } catch (_) {}
    };
    doPrefetch();
  }, [isSecPhase, departments, teachers, allUsers]);

  // Safety: never allow loading to persist beyond 3 seconds
  useEffect(() => {
    if (!loading) return;
    const safety = setTimeout(() => setLoading(false), 3000);
    return () => clearTimeout(safety);
  }, [loading]);

  const getDepartmentHeadLabel = useCallback((dept) => {
    if (!dept || !dept.subjectHeadId) return t('subjectAssignments.labels.notAssigned');
    if (String(dept.subjectHeadType || '').toLowerCase() === 'user') {
      return getTeacherName(dept.subjectHeadId);
    }
    if (String(dept.subjectHeadType || '').toLowerCase() === 'staff') {
      return t('subjectAssignments.labels.staffWithId', { id: dept.subjectHeadId });
    }
    return t('subjectAssignments.labels.notAssigned');
  }, [getTeacherName, t]);

  const handleViewDepartmentReport = useCallback((report) => {
    if (!report?.fileUrl) return;
    const url = resolveReportFileUrl(report.fileUrl);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [resolveReportFileUrl]);

  const handleDownloadDepartmentReport = useCallback((report) => {
    if (!report?.fileUrl) return;
    const url = resolveReportFileUrl(report.fileUrl);
    if (!url) return;

    const suggestedName = report.fileName || `department-report-${report.id || 'file'}`;

    fetch(url, { method: 'GET', credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Failed to download file (HTTP ${res.status})`);
        }
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = suggestedName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((e) => {
        try {
          const fallback = document.createElement('a');
          fallback.href = url;
          fallback.target = '_blank';
          fallback.rel = 'noreferrer';
          document.body.appendChild(fallback);
          fallback.click();
          document.body.removeChild(fallback);
        } catch (_) {}
        console.error('[DepartmentReports] Download failed', e);
      });
  }, [resolveReportFileUrl]);

  const handleDeleteDepartmentReport = useCallback(async (report) => {
    try {
      if (!report?.id) return;
      const confirmed = window.confirm(`Delete ${report.fileName || 'this report'}?`);
      if (!confirmed) return;
      setSaving(true);
      await fetchJson(`/api/departments/reports/${report.id}`, { method: 'DELETE' });
      await fetchData();
      enqueueSnackbar(t('subjectAssignments.messages.deleted'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    } catch (e) {
      const msg = (e && (e.message || e.error)) ? `${e.message || e.error}` : String(e);
      enqueueSnackbar(msg || 'Failed to delete department report', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [enqueueSnackbar, fetchData, t]);

  const handleOpenCreateDepartment = () => {
    setEditingDepartment(null);
    setDepartmentFormData({ name: '' });
    setDepartmentDialogOpen(true);
  };

  const handleOpenEditDepartment = (dept) => {
    setEditingDepartment(dept);
    setDepartmentFormData({ name: dept?.name || '' });
    setDepartmentDialogOpen(true);
  };

  const handleSaveDepartment = async () => {
    try {
      const name = String(departmentFormData?.name || '').trim();
      if (!name) {
        enqueueSnackbar(t('subjectAssignments.messages.nameRequired'), { variant: 'error' });
        return;
      }
      setSaving(true);
      const payload = { name };
      const isEdit = Boolean(editingDepartment?.id);
      const url = isEdit ? `/api/departments/${editingDepartment.id}` : '/api/departments';
      const method = isEdit ? 'PUT' : 'POST';
      const saved = await fetchJson(url, { method, body: payload });
      setDepartmentDialogOpen(false);
      setEditingDepartment(null);
      setDepartmentFormData({ name: '' });
      try { await fetchData(); } catch(_) {}
      enqueueSnackbar(t('subjectAssignments.messages.saved'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
      return saved;
    } catch (e) {
      const msg = (e && (e.message || e.error)) ? `${e.message || e.error}` : String(e);
      enqueueSnackbar(msg || t('subjectAssignments.messages.departmentSaveFailed'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDepartment = async (dept) => {
    try {
      if (!dept?.id) return;
      setSaving(true);
      await fetchJson(`/api/departments/${dept.id}`, { method: 'DELETE' });
      try { await fetchData(); } catch(_) {}
      enqueueSnackbar(t('subjectAssignments.messages.deleted'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    } catch (e) {
      const msg = (e && (e.message || e.error)) ? `${e.message || e.error}` : String(e);
      enqueueSnackbar(msg || t('subjectAssignments.messages.departmentDeleteFailed'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDepartmentSubjects = (deptId) => {
    const did = String(deptId || '');
    if (!did) return;
    setDeptSubjectsDepartmentId(did);
    const selected = dedupedSubjectGroups.filter(g => String(g.departmentId || '') === did).map(g => String(g.id));
    setDeptSubjectsSelectedGroupIds(selected);
    setDeptSubjectsDialogOpen(true);
  };

  const handleSaveDepartmentSubjects = async () => {
    try {
      if (!deptSubjectsDepartmentId) return;
      setSaving(true);
      const payload = { subjectGroupIds: (deptSubjectsSelectedGroupIds || []).map(Number).filter(Boolean) };
      await fetchJson(`/api/departments/${deptSubjectsDepartmentId}/subject-groups`, { method: 'PUT', body: payload });
      setDeptSubjectsDialogOpen(false);
      setDeptSubjectsDepartmentId('');
      setDeptSubjectsSelectedGroupIds([]);
      try { await fetchData(); } catch(_) {}
      enqueueSnackbar(t('subjectAssignments.messages.saved'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    } catch (e) {
      const msg = (e && (e.message || e.error)) ? `${e.message || e.error}` : String(e);
      enqueueSnackbar(msg || t('subjectAssignments.messages.departmentSubjectsSaveFailed'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeptHead = (dept) => {
    setDeptHeadDepartment(dept);
    setDeptHeadFormData({
      subjectHeadId: dept?.subjectHeadId ? String(dept.subjectHeadId) : ''
    });
    setDeptHeadDialogOpen(true);
  };

  const handleSaveDeptHead = async () => {
    try {
      if (!deptHeadDepartment?.id) return;
      setSaving(true);
      const payload = {
        subjectHeadType: 'user',
        subjectHeadId: deptHeadFormData.subjectHeadId ? Number(deptHeadFormData.subjectHeadId) : null
      };
      await fetchJson(`/api/departments/${deptHeadDepartment.id}/assign-head`, { method: 'PUT', body: payload });
      setDeptHeadDialogOpen(false);
      setDeptHeadDepartment(null);
      setDeptHeadFormData({ subjectHeadId: '' });
      try { await fetchData(); } catch(_) {}
      enqueueSnackbar(t('subjectAssignments.messages.saved'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    } catch (e) {
      const msg = (e && (e.message || e.error)) ? `${e.message || e.error}` : String(e);
      enqueueSnackbar(msg || t('subjectAssignments.messages.departmentHeadAssignFailed'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Teacher-Subject Assignment Functions
  const handleCloseTeacherSubjectDialog = () => {
    setTeacherSubjectDialogOpen(false);
    setSelectedTeacher('');
    setDialogTeacherSubjects([]);
  };
  const handleTeacherSubjectAssignment = (teacherId) => {
    setSelectedTeacher(teacherId);
    // Snapshot current assignments for this teacher into dialog-local state
    setDialogTeacherSubjects([...(teacherSubjectAssignments[teacherId] || [])]);
    setTeacherSubjectDialogOpen(true);
  };

  const handleTeacherSubjectChange = (subjectId, checked) => {
    setDialogTeacherSubjects(prev => {
      const current = Array.isArray(prev) ? prev : [];
      if (checked) {
        return current.includes(subjectId) ? current : [...current, subjectId];
      } else {
        return current.filter(id => id !== subjectId);
      }
    });
  };

  const handleSaveTeacherSubjects = async () => {
    try {
      setSaving(true);
      
      const currentAssignments = dialogTeacherSubjects || [];

      // Build headers for teacher-subject ops
      const tsSchoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const tsPhaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const tsPhaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const tsAyKey = tsSchoolId ? `${tsSchoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const tsAyId = localStorage.getItem(tsAyKey) || '';
      const tsToken = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
      const tsHeaders = {
        ...(tsSchoolId ? { 'X-Scope-School-Id': tsSchoolId } : {}),
        ...(tsPhaseCode ? { 'X-Active-Phase': tsPhaseCode } : {}),
        ...(tsPhaseId ? { 'X-Active-Phase-Id': tsPhaseId } : {}),
        ...(tsAyId ? { 'X-Academic-Year-Id': tsAyId } : {}),
        ...(tsToken ? { 'Authorization': `Bearer ${tsToken}` } : {}),
        'Accept': 'application/json'
      };

      // Remove existing assignments for this teacher
      const existingAssignments = await fetchJson(`/api/teacher-subjects?teacherId=${selectedTeacher}`, { headers: tsHeaders });
      const existingArray = Array.isArray(existingAssignments) ? existingAssignments : (existingAssignments?.rows || existingAssignments?.data || []);
      
      for (const assignment of existingArray) {
        await fetchJson(`/api/teacher-subjects/${assignment.id}`, { method: 'DELETE', headers: tsHeaders });
      }
      
      // Create new assignments
      if (currentAssignments.length > 0) {
        const assignmentsToCreate = currentAssignments.map(subjectId => ({
          teacherId: Number(selectedTeacher),
          subjectId: Number(subjectId)
        }));
        const postHeaders = { ...tsHeaders, 'Content-Type': 'application/json' };
        let ok = true;
        let bulkError = null;
        
        try {
          const bulkResult = await fetchJson('/api/teacher-subjects/bulk', {
            method: 'POST',
            headers: postHeaders,
            body: JSON.stringify(assignmentsToCreate)
          });
        } catch (err) {
          console.error('[TeacherSubjects] Bulk create FAILED with error:', err);
          console.error('[TeacherSubjects] Error details:', { message: err?.message, status: err?.status, statusText: err?.statusText });
          bulkError = err;
          ok = false;
        }
        
        if (!ok) {
          for (const item of assignmentsToCreate) {
            try {
              const result = await fetchJson('/api/teacher-subjects', {
                method: 'POST',
                headers: postHeaders,
                body: JSON.stringify(item)
              });
            } catch (err) {
              console.error('[TeacherSubjects] Individual create failed:', err);
              throw err;
            }
          }
        }
      }

      setTeacherSubjectAssignments(prev => {
        const next = { ...prev, [selectedTeacher]: currentAssignments };
        try {
          const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
          const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
          const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
          const ctx = `${schoolId}|${phaseId || phaseCode || '-'}`;
          const kTsMap    = `subjectAssignments:teacherSubjects:${ctx}`;
          localStorage.setItem(kTsMap, JSON.stringify(next));
        } catch (_) {}
        return next;
      });
      enqueueSnackbar(t('subjectAssignments.messages.teacherSubjectsSaved'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
      setTeacherSubjectDialogOpen(false);
      setSelectedTeacher('');
      setDialogTeacherSubjects([]);
      // Keep optimistic state; avoid immediate refetch that could wipe local state if server lags
      
    } catch (error) {
      console.error('[TeacherSubjects] Error updating teacher subjects:', error);
      const msg = (error && (error.message || error.error)) ? `${error.message || error.error}` : String(error);
      enqueueSnackbar(t('subjectAssignments.messages.teacherSubjectsSaveFailed', { message: msg }), { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    } finally {
      setSaving(false);
    }
  };

  // Removed getClassName (Class-Subject functionality removed)

  const getSubjectName = (subjectId) => {
    if (!subjectId || !Array.isArray(subjects)) return t('subjectAssignments.labels.unknown');
    const subject = subjects.find(s => s.id === subjectId);
    return subject && subject.name ? subject.name : t('subjectAssignments.labels.unknown');
  };

  // Only block if we truly have nothing to render yet (classes removed)
  if (loading && subjects.length === 0 && teachers.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <AppLoader variant="circular3" size={58} messages={[]} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/subject-management')}
          sx={{ mr: 2 }}
        >
          {t('common.back')}
        </Button>
        <Box>
          <Typography variant="h4" component="h1">
            {t('subjectAssignments.title')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('subjectAssignments.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof error === 'string' ? error : t('common.unexpectedError')}
        </Alert>
      )}

      {/* Debug Info removed per request */}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          {isSecPhase && <Tab value={TAB_DEPARTMENTS} label={t('common.departments') || 'Departments'} icon={<GroupIcon />} />}
          <Tab value={TAB_TEACHER_SUBJECTS} label={t('subjectAssignments.tabs.teacherSubjects')} icon={<GroupIcon />} />
        </Tabs>
      </Paper>

      {/* Tab 1: Departments */}
      {isSecPhase && activeTab === TAB_DEPARTMENTS && (
        <Paper sx={{ p: 3 }}>
          <Tabs value={departmentsTab} onChange={(e, v) => setDepartmentsTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2 }}>
            <Tab value={DEPT_TAB_DEPARTMENTS} label={t('common.departments') || 'Departments'} />
            <Tab value={DEPT_TAB_DEPARTMENT_SUBJECTS} label={t('common.subjects') || 'Department Subjects'} />
            <Tab value={DEPT_TAB_DEPARTMENT_HEADS} label={t('common.heads') || 'Department Heads'} />
            <Tab value={DEPT_TAB_DEPARTMENT_REPORTS} label={t('subjectAssignments.tabs.departmentReports') || 'Department Reports'} />
          </Tabs>

          {departmentsTab === DEPT_TAB_DEPARTMENTS && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" onClick={handleOpenCreateDepartment}>
                  {t('common.add')}
                </Button>
              </Box>

              <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
                <Table sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.name')}</TableCell>
                      <TableCell>{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(departments || []).map((dept) => (
                      <TableRow key={dept.id}>
                        <TableCell>{dept.name || t('common.unnamed')}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button startIcon={<EditIcon />} onClick={() => handleOpenEditDepartment(dept)} size="small" variant="outlined">
                              {t('common.edit')}
                            </Button>
                            <Button color="error" onClick={() => handleDeleteDepartment(dept)} size="small" variant="outlined">
                              {t('common.delete')}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {departmentsTab === DEPT_TAB_DEPARTMENT_SUBJECTS && (
            <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.department') || 'Department'}</TableCell>
                    <TableCell>{t('common.subjects') || 'Subjects'}</TableCell>
                    <TableCell>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(departments || []).map((dept) => {
                    const groups = dedupedSubjectGroups.filter(g => Number(g.departmentId) === Number(dept.id));
                    return (
                      <TableRow key={dept.id}>
                        <TableCell>{dept.name}</TableCell>
                        <TableCell>
                          {groups.length ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {groups.slice(0, 8).map(g => (
                                <Chip key={g.id} label={getSubjectGroupLabel(g)} size="small" color="primary" />
                              ))}
                              {groups.length > 8 && <Chip label={`+${groups.length - 8}`} size="small" variant="outlined" />}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">{t('subjectAssignments.noSubjectsAssigned')}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button startIcon={<AssignmentIcon />} onClick={() => handleOpenDepartmentSubjects(dept.id)} size="small" variant="outlined">
                            {t('common.manage')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {departmentsTab === DEPT_TAB_DEPARTMENT_HEADS && (
            <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.department') || 'Department'}</TableCell>
                    <TableCell>{t('common.head') || 'Head'}</TableCell>
                    <TableCell>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(departments || []).map((dept) => (
                    <TableRow key={dept.id}>
                      <TableCell>{dept.name}</TableCell>
                      <TableCell>{getDepartmentHeadLabel(dept)}</TableCell>
                      <TableCell>
                        <Button startIcon={<EditIcon />} onClick={() => handleOpenDeptHead(dept)} size="small" variant="outlined">
                          {t('common.assign')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {departmentsTab === DEPT_TAB_DEPARTMENT_REPORTS && (
            <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
              <Table sx={{ minWidth: 840 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.department') || 'Department'}</TableCell>
                    <TableCell>{t('common.month') || 'Month'}</TableCell>
                    <TableCell>{t('common.teacher') || 'Teacher'}</TableCell>
                    <TableCell>{t('common.file') || 'File'}</TableCell>
                    <TableCell>{t('common.date') || 'Date'}</TableCell>
                    <TableCell>{t('common.actions') || 'Actions'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(departmentReports || []).length ? (departmentReports || []).map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>{report.departmentName || '—'}</TableCell>
                      <TableCell>{formatDepartmentReportMonth(report.month, report.year)}</TableCell>
                      <TableCell>{report.uploadedByName || '—'}</TableCell>
                      <TableCell>{report.fileName || '—'}</TableCell>
                      <TableCell>{formatDepartmentReportDate(report.uploadedAt)}</TableCell>
                      <TableCell>
                        {report.fileUrl ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Tooltip title={t('common.open') || 'Open'}>
                              <span>
                                <IconButton size="small" color="primary" onClick={() => handleViewDepartmentReport(report)}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t('common.download') || 'Download'}>
                              <span>
                                <IconButton size="small" color="primary" onClick={() => handleDownloadDepartmentReport(report)}>
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t('common.delete') || 'Delete'}>
                              <span>
                                <IconButton size="small" color="error" disabled={saving} onClick={() => handleDeleteDepartmentReport(report)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="textSecondary">
                          {t('subjectAssignments.noDepartmentReports') || 'No departmental reports have been uploaded yet.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Tab 2: Teacher-Subject Assignment */}
      {activeTab === TAB_TEACHER_SUBJECTS && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('subjectAssignments.teacherSubjectAssignments')}
          </Typography>
          <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
            <Table sx={{ minWidth: 620 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('subjectAssignments.columns.teacher')}</TableCell>
                  <TableCell>{t('subjectAssignments.columns.assignedSubjects')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teachers.map((teacher) => {
                  const assignedSubjects = teacherSubjectAssignments[teacher.id] || [];
                  return (
                    <TableRow key={teacher.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Avatar
                            src={teacher.avatarUrl || ''}
                            sx={{ width: 28, height: 28, bgcolor: (teacher.avatarUrl ? 'transparent' : stringToColor(getDisplayName(teacher))), fontSize: 12 }}
                          >
                            {getInitials(getDisplayName(teacher))}
                          </Avatar>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 320, lineHeight: 1.2 }}>
                            {getDisplayName(teacher)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {assignedSubjects.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {assignedSubjects.map((subjectId) => (
                              <Chip
                                key={subjectId}
                                label={getSubjectName(subjectId)}
                                size="small"
                                color="primary"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {t('subjectAssignments.noSubjectsAssigned')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          startIcon={<AssignmentIcon />}
                          onClick={() => handleTeacherSubjectAssignment(teacher.id)}
                          size="small"
                          variant="outlined"
                        >
                          {t('common.manage')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Department Dialog */}
      <Dialog open={departmentDialogOpen} onClose={() => setDepartmentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDepartment ? (t('common.edit') || 'Edit') : (t('common.add') || 'Add')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={departmentFormData.name}
                onChange={(e) => setDepartmentFormData((prev) => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepartmentDialogOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSaveDepartment} variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} disabled={saving}>
            {saving ? t('common.saving') : t('subjectAssignments.actions.saveAndClose')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Department Subjects Dialog */}
      <Dialog open={deptSubjectsDialogOpen} onClose={() => setDeptSubjectsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('common.manage') || 'Manage'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t('common.subjects') || 'Subjects'}</InputLabel>
            <Select
              multiple
              value={deptSubjectsSelectedGroupIds}
              onChange={(e) => setDeptSubjectsSelectedGroupIds(e.target.value)}
              label={t('common.subjects') || 'Subjects'}
              renderValue={(selected) => {
                const ids = Array.isArray(selected) ? selected : [];
                const labels = ids
                  .map((id) => dedupedSubjectGroups.find((g) => String(g.id) === String(id)))
                  .filter(Boolean)
                  .map((g) => getSubjectGroupLabel(g));
                return labels.join(', ');
              }}
            >
              {dedupedSubjectGroups.map((g) => (
                <MenuItem key={g.id} value={String(g.id)}>
                  <Checkbox checked={(deptSubjectsSelectedGroupIds || []).includes(String(g.id))} />
                  <Typography variant="body2">{getSubjectGroupLabel(g)}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeptSubjectsDialogOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSaveDepartmentSubjects} variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} disabled={saving}>
            {saving ? t('common.saving') : t('subjectAssignments.actions.saveAndClose')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Department Head Dialog */}
      <Dialog open={deptHeadDialogOpen} onClose={() => setDeptHeadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('common.assign') || 'Assign'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('common.teacher') || 'Teacher'}</InputLabel>
                <Select
                  value={deptHeadFormData.subjectHeadId}
                  label={t('common.teacher') || 'Teacher'}
                  onChange={(e) => setDeptHeadFormData((prev) => ({ ...prev, subjectHeadId: e.target.value }))}
                >
                  <MenuItem value=""><em>{t('subjectAssignments.labels.notAssigned')}</em></MenuItem>
                  {(teachers || []).map((u) => (
                    <MenuItem key={u.id} value={String(u.id)}>{getDisplayName(u)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeptHeadDialogOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSaveDeptHead} variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} disabled={saving}>
            {saving ? t('common.saving') : t('subjectAssignments.actions.saveAndClose')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Teacher-Subject Assignment Dialog */}
      <Dialog open={teacherSubjectDialogOpen} onClose={handleCloseTeacherSubjectDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('subjectAssignments.dialogs.manageTeacherSubjects.title')}
                     {selectedTeacher && (
            <Typography variant="body2" color="textSecondary">
              {getTeacherName(selectedTeacher) || t('subjectAssignments.dialogs.manageTeacherSubjects.unknownTeacher')}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('subjectAssignments.dialogs.manageTeacherSubjects.instructions')}
          </Typography>
          <Grid container spacing={2}>
            {subjects.map((subject) => (
              <Grid item xs={12} md={6} key={subject.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(dialogTeacherSubjects || []).includes(subject.id)}
                      onChange={(e) => handleTeacherSubjectChange(subject.id, e.target.checked)}
                    />
                  }
                  label={`${subject.name || t('common.unnamedSubject')} (${subject.code || t('common.noCode')})`}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTeacherSubjectDialog} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveTeacherSubjects}
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? t('common.saving') : t('subjectAssignments.actions.saveAndClose')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubjectAssignmentsPage;


